<template>
  <div class="flex flex-col h-full bg-white">
    <!-- 상단 고정: 검색/전체선택/개수 (sticky) -->
    <div class="sticky top-0 z-10 bg-white">
      <div class="flex items-center gap-2 px-2 pt-6 pb-2 mr-2">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="채팅 기록 검색 ..."
          class="flex-1 px-4 py-2 mr-2 ml-6 border-2 border-gray-300 rounded-lg focus:outline-none focus:shadow-lg focus:border-gray-300 transition-shadow duration-200"
          title="채팅 기록 검색"
        />
        <button
          v-if="selectedChats.length > 0"
          @click="deleteSelected"
          class="px-3 py-3 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 ml-1"
          title="선택한 채팅 삭제"
        >
          삭제
        </button>
      </div>
      <div class="flex items-center gap-4 px-7 pt-4 pb-2">
        <input
          type="checkbox"
          :checked="isAllSelected"
          @change="toggleSelectAll"
          class="h-4 w-4 ml-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          title="전체 선택"
        />
        <span title="전체 선택">전체 선택</span>
        <span class="text-sm text-gray-600 ml-2">총 {{ filteredChats.length }}개</span>
      </div>
    </div>
    <!-- 채팅 목록(히스토리)만 스크롤 -->
    <div class="flex-1 overflow-y-auto px-6 pb-6">
      <div v-if="isLoading" class="flex items-center justify-center h-full">
        <span class="loader spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-blue-400 border-t-transparent"></span>
      </div>
      <div v-else>
        <div v-if="filteredChats.length === 0">
          <div v-if="searchQuery" class="text-center text-gray-500 py-4 bg-white">
            '{{ searchQuery }}'과(와) 일치하는 대화가 0개 있습니다.
          </div>
          <div v-else class="text-center text-gray-500 py-4 bg-white">
            대화 내역이 없습니다.
          </div>
        </div>
        <div v-else>
          <div v-for="([date, chats]) in groupedChatsByDate" :key="date">
            <div class="text-xm text-gray-400 font-semibold mt-1 mb-1 pl-1 rounded px-3 py-1 w-fit">{{ date }}</div>
            <div v-for="chat in chats" :key="chat.id"
              class="p-2 pl-1 cursor-pointer bg-white hover:bg-blue-50 rounded-lg"
              @click="$emit('select-chat', chat.id)"
              title="채팅 보기"
            >
              <div class="flex items-center">
                <div class="mr-4" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isSelected(chat.id)"
                    @click.stop="toggleSelect(chat.id)"
                    class="h-4 w-4 ml-2 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    title="채팅 선택"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-gray-900 mt-1 ellipsis-title">{{ chat.title }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 삭제 확인 모달 추가 -->
    <AlertModal 
      :visible="showDeleteModal"
      message="선택한 채팅을 삭제하시겠습니까?"
      type="confirm"
      :isDeleting="isDeleting"
      @confirm="confirmDelete"
      @cancel="cancelDelete"
    />
  </div>
</template>

<script>
import AlertModal from '@/components/common/AlertModal.vue'

export default {
  components: {
    AlertModal
  },
  name: 'ChatHistoryViewAll',
  props: {
    chats: { type: Array, required: true },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      selectedChats: [],
      showDeleteModal: false,
      isDeleting: false
    }
  },
  computed: {
    filteredChats() {
      if (!this.searchQuery) return this.chats
      const query = this.searchQuery.toLowerCase()
      return this.chats.filter(chat =>
        chat.title.toLowerCase().includes(query)
      )
    },
    groupedChatsByDate() {
      const groups = {};
      this.filteredChats.forEach(chat => {
        const date = chat.created_at ? chat.created_at.slice(0, 10) : '';
        if (!groups[date]) groups[date] = [];
        groups[date].push(chat);
      });
      return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
    },
    isAllSelected() {
      return this.filteredChats.length > 0 && this.selectedChats.length === this.filteredChats.length
    }
  },
  methods: {
    handleChatClick(chatId) {
      this.$emit('select-chat', chatId);
    },
    toggleSelect(chatId) {
      const index = this.selectedChats.indexOf(chatId);
      if (index === -1) {
        this.selectedChats.push(chatId);
      } else {
        this.selectedChats.splice(index, 1);
      }
    },
    isSelected(chatId) {
      return this.selectedChats.includes(chatId);
    },
    deleteSelected() {
      if (this.selectedChats.length === 0) return;
      this.showDeleteModal = true;
    },
    confirmDelete() {
      this.isDeleting = true;
      this.$emit('delete-selected', this.selectedChats);
    },
    cancelDelete() {
      if (this.isDeleting) {
        // 삭제 중 취소는 일단 무시 (또는 나중에 삭제 중단 로직 추가 가능)
        return;
      }
      this.showDeleteModal = false;
      this.isDeleting = false;
    },
    // 삭제 완료 시 호출될 메서드
    onDeleteCompleted() {
      this.selectedChats = [];
      this.showDeleteModal = false;
      this.isDeleting = false;
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedChats = [];
      } else {
        this.selectedChats = this.filteredChats.map(chat => chat.id);
      }
    }
  }
}
</script>

<style scoped>
.ellipsis-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 900px;
  width: 100%;
  display: block;
}
.loader {
  border-width: 4px;
  border-style: solid;
  border-radius: 50%;
  border-color: #60a5fa #60a5fa #60a5fa transparent;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style> 